/**
 * A detail panel that opens on hover, and on keyboard focus.
 *
 * The first version of this looked like a broken card: it used the same surface colour
 * as everything around it and sat in the middle of the grid with no shadow and no
 * arrow. It read as a layout bug, not as an explanation.
 *
 * What the popover research actually asks for, and what this now does:
 *   - a contrasting, raised surface with a real drop shadow, so it reads as floating
 *     above the page rather than being part of it;
 *   - an arrow pointing at whatever it belongs to. NN/g calls for this specifically
 *     when several elements sit close together, which is exactly a grid of cards;
 *   - it never renders off-screen: it flips above when it is near the bottom and is
 *     nudged back inside the window when it would run off an edge;
 *   - a short delay before opening, so it does not flash at every card the cursor
 *     crosses on its way somewhere else;
 *   - it opens on focus as well as hover, so someone tabbing through gets the same
 *     information a mouse user gets;
 *   - the panel never takes pointer events, so it can never sit between a cursor and
 *     the thing the cursor was heading for.
 */
import { ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react'
import './HoverDetail.css'

/** Long enough not to flicker on a passing cursor, short enough not to feel broken. */
const OPEN_DELAY_MS = 130

interface Props {
  detail: ReactNode
  children: ReactNode
  className?: string
  suppressed?: boolean
}

export default function HoverDetail({ detail, children, className, suppressed }: Props) {
  const [open, setOpen] = useState(false)
  const [flip, setFlip] = useState(false)
  const [shift, setShift] = useState(0)
  const wrap = useRef<HTMLSpanElement | null>(null)
  const panel = useRef<HTMLSpanElement | null>(null)
  const timer = useRef<number | null>(null)
  const id = useId()

  const clearTimer = () => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current)
      timer.current = null
    }
  }

  const show = useCallback(() => {
    if (suppressed || !detail) return
    clearTimer()
    timer.current = window.setTimeout(() => {
      const el = wrap.current
      if (el) {
        const box = el.getBoundingClientRect()
        setFlip(window.innerHeight - box.bottom < 260)
      }
      setOpen(true)
    }, OPEN_DELAY_MS)
  }, [suppressed, detail])

  const hide = useCallback(() => {
    clearTimer()
    setOpen(false)
    setShift(0)
  }, [])

  useEffect(() => clearTimer, [])

  /* Nudge back inside the window if it would run off an edge. Done after it renders,
     because until then there is nothing to measure. */
  useEffect(() => {
    if (!open) return
    const el = panel.current
    if (!el) return
    const box = el.getBoundingClientRect()
    const margin = 12
    if (box.right > window.innerWidth - margin) {
      setShift(window.innerWidth - margin - box.right)
    } else if (box.left < margin) {
      setShift(margin - box.left)
    }
  }, [open])

  return (
    <span
      ref={wrap}
      className={className ? 'hd-wrap ' + className : 'hd-wrap'}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocusCapture={show}
      onBlurCapture={hide}
      onKeyDown={(e) => {
        if (e.key === 'Escape') hide()
      }}
      aria-describedby={open ? id : undefined}
    >
      {children}
      {open ? (
        <span
          ref={panel}
          className={flip ? 'hd-panel hd-flip' : 'hd-panel'}
          style={shift ? { marginLeft: shift } : undefined}
          id={id}
          role="tooltip"
        >
          <span className="hd-arrow" />
          {detail}
        </span>
      ) : null}
    </span>
  )
}
